import type { ContactFieldDto, ContactStatusDto } from '@carbonwebmail/contracts'
import { needsChoices } from '@carbonwebmail/domain'
import type { FieldProps } from '../../ui/Field.js'
import { Field, SelectField } from '../../ui/Field.js'

/**
 * What a contact is, as a form.
 *
 * Shared by the dialog that adds one and the dialog that edits one, because
 * they ask for the same thing: a name, a company, a subscription state, and
 * every field this organisation declared. Two copies would be two places for a
 * declared field to stop appearing, and the one that drifted would be the one
 * somebody filled in.
 *
 * The address is not here. Adding a contact needs one and editing must not
 * change one — see `EditContactDialog` — so it belongs to each caller.
 *
 * **One field per row.** These were two columns, which put a label beside an
 * unrelated value and made a long form read as a grid of things to fill in
 * rather than a sequence. The dialog scrolls when there are more than fit.
 */
export function ContactFields({
  idPrefix,
  name,
  company,
  status,
  statuses,
  custom,
  fields,
  busy,
  onName,
  onCompany,
  onStatus,
  onCustom,
}: {
  /** So two of these on one page cannot share an element id. */
  readonly idPrefix: string
  readonly name: string
  readonly company: string
  readonly status: ContactStatusDto
  readonly statuses: readonly (readonly [string, string])[]
  readonly custom: Readonly<Record<string, string>>
  readonly fields: readonly ContactFieldDto[]
  readonly busy: boolean
  readonly onName: (value: string) => void
  readonly onCompany: (value: string) => void
  readonly onStatus: (value: ContactStatusDto) => void
  readonly onCustom: (key: string, value: string) => void
}) {
  return (
    <>
      <Field
        disabled={busy}
        id={`${idPrefix}-name`}
        label="Name"
        onChange={onName}
        optional
        type="text"
        value={name}
      />
      <Field
        disabled={busy}
        id={`${idPrefix}-company`}
        label="Company"
        onChange={onCompany}
        optional
        type="text"
        value={company}
      />
      {/*
        Offered, because a list brought from somewhere else can legitimately
        hold people who have already unsubscribed — and saying so is the only
        way that fact is ever recorded honestly. Changing it on an existing
        contact writes a consent event.
      */}
      <SelectField
        disabled={busy}
        id={`${idPrefix}-status`}
        label="Subscription"
        onChange={(value) => onStatus(value as ContactStatusDto)}
        options={statuses}
        value={status}
      />

      {fields.map((field) => (
        <Declared
          busy={busy}
          field={field}
          id={`${idPrefix}-${field.key}`}
          key={field.key}
          onChange={(value) => onCustom(field.key, value)}
          value={custom[field.key] ?? ''}
        />
      ))}
    </>
  )
}

/** The three states a contact can be in, as a picker reads them. */
export const STATES: readonly (readonly [string, string])[] = [
  ['subscribed', 'Subscribed'],
  ['unsubscribed', 'Unsubscribed'],
  ['cleaned', 'Cleaned'],
]

/**
 * A declared field, asked for the way its kind wants to be asked.
 *
 * Exported because the selection bar sets one of these across many contacts and
 * has to ask for the value exactly as the contact form does. A second control
 * per kind would be a second answer to what a status column offers, and the one
 * that drifted would be the one somebody filled in.
 */
export function Declared({
  field,
  id,
  value,
  busy,
  onChange,
}: {
  readonly field: ContactFieldDto
  readonly id: string
  readonly value: string
  readonly busy: boolean
  readonly onChange: (value: string) => void
}) {
  if (needsChoices(field.type)) {
    return (
      <SelectField
        disabled={busy}
        id={id}
        label={field.label}
        onChange={onChange}
        options={[['', 'Not set'], ...field.choices.map((choice) => [choice, choice] as const)]}
        value={value}
      />
    )
  }

  return (
    <Field
      disabled={busy}
      id={id}
      label={field.label}
      onChange={onChange}
      optional
      type={INPUT[field.type]}
      value={value}
    />
  )
}

/**
 * What a kind is typed into.
 *
 * `email`, `phone` and `url` get the right keyboard on a phone and nothing
 * more — nothing validates them, so nothing claims to. `boolean` has no control
 * of its own yet and is typed, which is honest about what the kind currently
 * does rather than drawing a tick box that stores the word "true".
 */
const INPUT: Record<ContactFieldDto['type'], FieldProps['type']> = {
  text: 'text',
  number: 'number',
  date: 'date',
  boolean: 'text',
  email: 'email',
  phone: 'tel',
  url: 'url',
  dropdown: 'text',
  status: 'text',
}
